import { z } from 'zod';

/**
 * Project variables — the server-backed replacement for the editor's
 * `EnvironmentManager` localStorage store. Used by:
 *   - the script step (Phase 1) when an output declares `scope: 'project'`
 *   - the run engine on flow execution (resolves `{{varName}}` literals)
 *   - the EnvironmentManager UI (Phase 2b)
 *
 * Wire shape only; the backing table is `project_variables` (see
 * migration 040). Secrets are encrypted at rest via the crypto-vault
 * AES-256-GCM helper and never returned in list responses — callers
 * must hit a dedicated reveal endpoint that audit-logs the access.
 */

export const ProjectVariableTypeSchema = z.enum(['string', 'number', 'boolean', 'array', 'object', 'date']);
export type ProjectVariableType = z.infer<typeof ProjectVariableTypeSchema>;

export const ProjectVariableScopeSchema = z.enum(['project', 'environment']);
export type ProjectVariableScope = z.infer<typeof ProjectVariableScopeSchema>;

/** Restrict variable names to identifier-safe characters so the runner
 *  can interpolate them without escaping concerns. Mirrors the
 *  validation already enforced by `ScriptOutputSchema`. */
export const VARIABLE_NAME_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const ProjectVariableSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string().min(1).max(64).regex(VARIABLE_NAME_REGEX),
  type: ProjectVariableTypeSchema.default('string'),
  scope: ProjectVariableScopeSchema.default('project'),
  /** Free-text id matching the editor's `EnvironmentEntry.id`
   *  (kebab-case like 'staging'). Always null for project-scope. */
  environmentId: z.string().nullable(),
  description: z.string().max(500).nullable(),
  isSecret: z.boolean().default(false),
  /**
   * For non-secrets: the plaintext value (may be empty string for "set
   * but blank"). For secrets: ALWAYS null in list responses. The
   * dedicated reveal endpoint includes the decrypted value.
   */
  value: z.string().nullable(),
  /** Convenience: true when a value is set on the server. Lets the UI
   *  render "•••• (set)" without needing the actual ciphertext. */
  hasValue: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectVariable = z.infer<typeof ProjectVariableSchema>;

export const ProjectVariableListResponseSchema = z.object({
  variables: z.array(ProjectVariableSchema),
});
export type ProjectVariableListResponse = z.infer<typeof ProjectVariableListResponseSchema>;

export const CreateProjectVariableRequestSchema = z.object({
  name: z.string().min(1).max(64).regex(VARIABLE_NAME_REGEX),
  value: z.string().max(100_000).optional(),
  type: ProjectVariableTypeSchema.optional(),
  scope: ProjectVariableScopeSchema.optional(),
  environmentId: z.string().min(1).max(64).nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  isSecret: z.boolean().optional(),
});
export type CreateProjectVariableRequest = z.infer<typeof CreateProjectVariableRequestSchema>;

/** Partial body for PATCH-style updates. Name changes flow through a
 *  separate endpoint (rename) so the audit trail can capture them. */
export const UpdateProjectVariableRequestSchema = z.object({
  value: z.string().max(100_000).optional(),
  type: ProjectVariableTypeSchema.optional(),
  scope: ProjectVariableScopeSchema.optional(),
  environmentId: z.string().min(1).max(64).nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  isSecret: z.boolean().optional(),
});
export type UpdateProjectVariableRequest = z.infer<typeof UpdateProjectVariableRequestSchema>;

export const ProjectVariableHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  variableId: z.string().uuid().nullable(),
  variableName: z.string(),
  variableEnvironmentId: z.string().nullable(),
  changeAction: z.enum(['CREATE', 'UPDATE', 'DELETE']),
  changedByUserId: z.string().uuid().nullable(),
  changedByEmail: z.string().nullable(),
  /** Plaintext snapshots are present only for non-secret variables.
   *  For secrets the values are masked here and only revealed on the
   *  dedicated audit-logged reveal endpoint. */
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  oldIsSecret: z.boolean().nullable(),
  newIsSecret: z.boolean().nullable(),
  oldType: ProjectVariableTypeSchema.nullable(),
  newType: ProjectVariableTypeSchema.nullable(),
  createdAt: z.string(),
});
export type ProjectVariableHistoryEntry = z.infer<typeof ProjectVariableHistoryEntrySchema>;

export const ProjectVariableHistoryResponseSchema = z.object({
  entries: z.array(ProjectVariableHistoryEntrySchema),
});
export type ProjectVariableHistoryResponse = z.infer<typeof ProjectVariableHistoryResponseSchema>;
